import {
  Alert,
  AlertIcon,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import FormRemoteSelectInput from 'components/FormRemoteSelectInput';
import FormWrapper from 'components/FormWrapper';
import InputForm from 'components/InputForm';
import InputNumber from 'components/InputNumber';
import SelectForm from 'components/SelectForm';
import useAuth from 'contexts/useAuth';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { createWithdrawal, findAllParticipants } from 'services/api.service';
import { validCnpj, validCpf } from 'services/validDocument';
import { maskCurrency } from 'utils/number';
import { onlyNumbers } from 'utils/string';
import { useTwoFaHelper } from 'contexts/TwoFaCheckContext';
import CustomButton from 'components/CustomButton';
import ScheduledWithdrawalAlert from 'components/ScheduledWithdrawalAlert';
import { queryClient } from 'index';
import { mask } from 'utils/mask';
import FormLabelCustom from 'components/FormLabel';

const CreateWithdrawel: React.FC<{
  onClose: () => void;
  asPage?: boolean;
}> = ({ onClose, asPage }) => {
  const toast = useToast();
  const { userData, updateUserData } = useAuth();
  const [loading, setLoading] = React.useState(false);

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm<{
    ispb: string;
    amount: number;
    account: string;
    description: string;
    agency: string;
    accountHolderDocument: string;
    accountHolderName: string;
    accountType: string;
    dueDate: string;
    pixKeyType: string;
    keyPix: string;
  }>();

  const pixKeyType = watch('pixKeyType');

  const amountValue = Number(watch('amount') || 0);
  const realAvailable = +userData.balance - +userData.amountLocked;
  const accountLimit = +(userData.accountLimit || 0);
  const availableWithLimit = realAvailable + accountLimit;
  const usingLimit = accountLimit > 0 && amountValue > realAvailable;

  const applyPixKeyMask = (value: string, type: string): string => {
    const digits = onlyNumbers(value);
    switch (type) {
      case 'CPF':
        return mask(digits, '999.999.999-99');
      case 'CNPJ':
        return mask(digits, '99.999.999/9999-99');
      case 'PHONE':
        return mask(digits, digits.length > 10 ? '(99) 99999-9999' : '(99) 9999-9999');
      default:
        return value;
    }
  };

  const getPixKeyRaw = (value: string, type: string): string => {
    if (!value) return '';
    const digits = onlyNumbers(value);
    switch (type) {
      case 'CPF':
      case 'CNPJ':
        return digits;
      case 'PHONE':
        return `+55${digits}`;
      case 'EMAIL':
        return value.trim().toLowerCase();
      case 'EVP':
        return value.trim().toLowerCase();
      default:
        return value;
    }
  };

  const { requestTwoFa } = useTwoFaHelper();

  const createwithdrawel = async (data: any) => {
    const code = await requestTwoFa();
    if (!code?.id) {
      return;
    }

    setLoading(true);

    try {
      await createWithdrawal(code.id, {
        ...data,
        accountHolderDocument: data.accountHolderDocument ? onlyNumbers(data.accountHolderDocument) : undefined,
        ispb: data.ispb?.value,
        type: 'MANU',
        keyPix: data.keyPix ? getPixKeyRaw(data.keyPix, data.pixKeyType) : undefined,
      });
      updateUserData();
      queryClient.refetchQueries({ queryKey: ['findAllWithdrawers'] });

      toast({
        title: 'Sucesso',
        description: 'Saque criado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao criar saque',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormWrapper
      title="Criar Saque"
      onClose={onClose}
      asPage={asPage}
      isLoading={loading}
      footer={
        <CustomButton
          colorScheme="green"
          isLoading={loading}
          onClick={handleSubmit(createwithdrawel)}
        >
          Criar
        </CustomButton>
      }
    >
      <ScheduledWithdrawalAlert />
      <Text fontWeight="semibold">Dados bancários</Text>

      <SelectForm
        control={control}
        label="Tipo de chave Pix"
        name="pixKeyType"
        placeholder="Selecione o tipo"
        options={[
          { label: 'CPF', value: 'CPF' },
          { label: 'CNPJ', value: 'CNPJ' },
          { label: 'E-mail', value: 'EMAIL' },
          { label: 'Telefone', value: 'PHONE' },
          { label: 'Chave aleatória (EVP)', value: 'EVP' },
        ]}
        onChange={() => {
          setValue('keyPix', '');
        }}
      />

      {pixKeyType && (
        <Controller
          control={control}
          name="keyPix"
          rules={{
            validate: {
              valid: (value) => {
                if (!value) return true;
                const digits = onlyNumbers(value);
                switch (pixKeyType) {
                  case 'CPF':
                    return (digits.length === 11 && validCpf(digits)) || 'CPF inválido';
                  case 'CNPJ':
                    return (digits.length === 14 && validCnpj(digits)) || 'CNPJ inválido';
                  case 'PHONE':
                    return (digits.length >= 10 && digits.length <= 11) || 'Telefone inválido';
                  case 'EMAIL':
                    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'E-mail inválido';
                  case 'EVP':
                    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) || 'Chave aleatória inválida';
                  default:
                    return true;
                }
              },
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <FormControl isInvalid={!!error} w="full">
              <FormLabelCustom label="Chave Pix" name="keyPix" props={{}} />
              <Input
                {...field}
                w="full"
                size="sm"
                bg="white"
                placeholder={
                  pixKeyType === 'CPF' ? '000.000.000-00'
                    : pixKeyType === 'CNPJ' ? '00.000.000/0000-00'
                    : pixKeyType === 'PHONE' ? '(00) 00000-0000'
                    : pixKeyType === 'EMAIL' ? 'email@exemplo.com'
                    : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
                }
                maxLength={
                  pixKeyType === 'CPF' ? 14
                    : pixKeyType === 'CNPJ' ? 18
                    : pixKeyType === 'PHONE' ? 15
                    : undefined
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (['CPF', 'CNPJ', 'PHONE'].includes(pixKeyType)) {
                    field.onChange(applyPixKeyMask(val, pixKeyType));
                  } else {
                    field.onChange(val);
                  }
                }}
              />
              <FormErrorMessage>{error?.message}</FormErrorMessage>
            </FormControl>
          )}
        />
      )}

      <InputForm
        control={control}
        label="Documento recebedor"
        placeholder="CPF ou CNPJ"
        name="accountHolderDocument"
        type="text"
        rules={{
          required: 'Campo obrigatório',
          validate: {
            valid: (value) => {
              value = onlyNumbers(value);

              return validCpf(value) || validCnpj(value) || 'Documento inválido';
            },
          },
        }}
      />
      <InputForm
        control={control}
        placeholder="Nome do recebedor"
        label="Nome do recebedor"
        name="accountHolderName"
        type="text"
      />

      <FormRemoteSelectInput
        control={control}
        label="Instituição financeira"
        required
        registerOptions={{
          required: 'Esse campo é obrigatório',
        }}
        loadDataFn={({ value, cb }) =>
          findAllParticipants({ filter: value }).then((retorno) => {
            if (retorno?.registers?.length > 0) {
              cb(
                retorno.registers?.map((d: any) => ({
                  label: d.reducedName,
                  value: d.ispb,
                })),
              );
            } else {
              cb([]);
            }
          })
        }
        name="ispb"
      />
      <SelectForm
        control={control}
        label="Tipo de conta"
        name="accountType"
        options={[
          {
            label: 'Conta Corrente',
            value: 'CACC',
          },
          {
            label: 'Conta de Pagamento',
            value: 'TRAN',
          },
          {
            label: 'Conta Poupança',
            value: 'SVGS',
          },
        ]}
      />
      <FormControl isInvalid={!!errors.agency?.message}>
        <FormLabel
          display="flex"
          ms="4px"
          fontSize="sm"
          mb="8px"
        >
          Agência<Text>*</Text>
        </FormLabel>
        <Input
          isRequired={true}
          variant="auth"
          fontSize="sm"
          maxLength={4}
          name="agency"
          {...register('agency', {
            required: 'Esse campo é obrigatório',
            minLength: {
              message: 'Esse campo deve ter no mínimo 4 caracteres',
              value: 4,
            },
            maxLength: {
              message: 'Esse campo deve ter no máximo 4 caracteres',
              value: 4,
            },
          })}
          ms={{ base: '0px', md: '0px' }}
          type="number"
          placeholder="0001"
        />
        <FormErrorMessage>{errors.agency?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.account?.message}>
        <FormLabel
          display="flex"
          ms="4px"
          fontSize="sm"
          mb="8px"
        >
          Conta (com dígito)<Text>*</Text>
        </FormLabel>
        <Input
          isRequired={true}
          variant="auth"
          fontSize="sm"
          name="account"
          {...register('account', {
            required: 'Esse Campo é Obrigatório',
          })}
          ms={{ base: '0px', md: '0px' }}
          type="tel"
          placeholder="00101"
        />
        <FormErrorMessage>{errors.account?.message}</FormErrorMessage>
      </FormControl>

      <InputNumber
        control={control}
        percentage={false}
        placeholder="R$ 0,00"
        label={'Valor'}
        name={`amount`}
        rules={{
          required: 'Campo obrigatório',
          validate: {
            positive: (value) => value > 0 || 'O valor deve ser maior que 0',
            max: (value) =>
              value <= availableWithLimit ||
              (accountLimit > 0
                ? 'O valor excede o saldo disponível somado ao limite de conta'
                : 'O valor deve ser menor que o saldo disponível'),
          },
        }}
      />
      <VStack align="start" spacing={1} w="full">
        <Text>
          Saldo disponível: <b>{maskCurrency(realAvailable)}</b>
        </Text>
        {accountLimit > 0 && (
          <Text fontSize="sm" color="gray.600">
            Limite de conta: <b>{maskCurrency(accountLimit)}</b>
          </Text>
        )}
      </VStack>
      {usingLimit && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            {realAvailable < 0
              ? `Seu saldo está negativo. Este saque utilizará o limite de conta (limite de ${maskCurrency(accountLimit)}).`
              : `Este saque utilizará ${maskCurrency(
                  Math.min(amountValue - realAvailable, accountLimit),
                )} do seu limite de conta.`}
          </Text>
        </Alert>
      )}
      <FormControl isInvalid={!!errors.description?.message}>
        <FormLabel
          display="flex"
          ms="4px"
          fontSize="sm"
          mb="8px"
        >
          Descrição<Text>*</Text>
        </FormLabel>
        <Input
          isRequired={true}
          variant="auth"
          fontSize="sm"
          size="sm"
          {...register('description', {
            maxLength: {
              value: 100,
              message: 'O limite de caracteres é de 100',
            },
          })}
          ms={{ base: '0px', md: '0px' }}
          type="text"
          rounded="sm"
          placeholder="Pagamento de contas"
        />
        <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
      </FormControl>
    </FormWrapper>
  );
};

export default CreateWithdrawel;
